<template>
  <b-col class="col-sm-12 mx-auto p-3">
    <h3 class="mb-3">
      Thank you for verifying your email address. Please complete the steps
      below to fill out your profile. Once completed you will be forwarded to
      your profile page.
    </h3>

    <v-stepper ref="stepper" :steps="steps" v-model="step">
      <template slot="step-1-index" slot-scope="scope">
        <div class="step-index">{{ scope.displayIndex }}</div>
      </template>
      <template slot="step-1">Plan</template>
      <template slot="step-2">Review</template>
      <template slot="step-3">Pay</template>
    </v-stepper>

    <template v-if="step === 1">
      <p>Select a package</p>
      <b-row>
        <b-col cols="12" md="4" v-for="plan in plans" :key="plan.name">
          <b-card>
            <b-card-header class="text-center">
              <h4 class="font-weight-bold">{{ plan.name }}</h4>
            </b-card-header>
            <b-card-body class="mb-4">
              <h3 class="b-card-title text-center" v-if="plan.price > 0">
                ${{ plan.price / 100 }}
                <small class="text-muted">/ mo</small>
              </h3>
              <h3 class="b-card-title text-center" v-else>Free</h3>
              <b-list-group flush>
                <b-list-group-item>
                  {{ plan.jobs }} {{ plan.jobs === 1 ? "Job" : "Jobs" }} per
                  month
                  <font-awesome-icon
                    :icon="['fas', 'check']"
                    class="m-auto text-success"
                  />
                </b-list-group-item>
                <b-list-group-item>
                  {{ plan.questions }} Questions per questionnaire
                  <font-awesome-icon
                    :icon="['fas', 'check']"
                    class="m-auto text-success"
                  />
                </b-list-group-item>
                <b-list-group-item>
                  {{ plan.logins }} {{ plan.logins === 1 ? "Login" : "Logins" }}
                  <font-awesome-icon
                    :icon="['fas', 'check']"
                    class="m-auto text-success"
                  />
                </b-list-group-item>
              </b-list-group>
            </b-card-body>
            <b-button
              size="lg"
              block
              variant="primary"
              @click="selectPlan(plan.name)"
              >Select Plan</b-button
            >
          </b-card>
        </b-col>
      </b-row>
    </template>

    <template v-if="step === 2">
      <p>Review</p>
      <b-row>
        <b-col cols="12">
          <p>User Information</p>
          <p class="text-muted">
            Please fill in the information below regarding company and contact
            information
          </p>
          <b-row>
            <b-col cols="12" md="6" class="mt-5">
              <label>First Name:</label>
              <b-form-input v-model="user.fname" placeholder="First Name" />
            </b-col>
            <b-col cols="12" md="6" class="mt-5">
              <label>Last Name:</label>
              <b-form-input v-model="user.lname" placeholder="Last Name" />
            </b-col>
            <b-col cols="12" class="mt-5">
              <label>Company Name:</label>
              <b-form-input v-model="company.name" placeholder="Company Name" />
            </b-col>
            <b-col cols="12" class="mt-5">
              <label>Address: Use this to autocomplete your address.</label>
              <google-autocomplete
                id="map"
                class="form-control"
                types="geocode"
                placeholder="Start typing"
                v-model="company.address.formatted"
                :value="user.address"
                v-on:placechanged="getAddressData"
              />
            </b-col>
            <b-col cols="12" class="mt-5">
              <p>Please review the data below and ensure it is correct.</p>
            </b-col>
            <b-col cols="12" md="6" class="mt-3">
              <label>Unit:</label>
              <b-form-input
                v-model="company.address.line2"
                placeholder="101, E, etc..."
              />
            </b-col>
            <b-col cols="12" md="6" class="mt-3">
              <label>Line 1:</label>
              <b-form-input
                v-model="company.address.line1"
                placeholder="123 Playground Street"
              />
            </b-col>
            <b-col cols="12" md="6" class="mt-5">
              <label>City:</label>
              <b-form-input
                v-model="company.address.city"
                placeholder="San Francisco"
              />
            </b-col>
            <b-col cols="12" md="6" class="mt-5">
              <label>Province/State:</label>
              <b-form-select
                :options="
                  company.address.country === 'CA' ||
                  company.address.country === 'Canada'
                    ? provinces
                    : states
                "
                v-model="company.address.state"
              >
                <b-form-select-option value
                  >Please select a country to populate the
                  list</b-form-select-option
                >
              </b-form-select>
            </b-col>
            <b-col cols="12" md="6" class="mt-5">
              <label>Postal Code:</label>
              <b-form-input
                v-model="company.address.postal_code"
                placeholder="L0L 0L0 or 12345"
              />
            </b-col>
            <b-col cols="12" md="6" class="mt-5">
              <label>Country:</label>
              <b-form-input
                v-model="company.address.country"
                placeholder="Canada, United States, etc..."
              />
            </b-col>
          </b-row>
        </b-col>
        <b-col cols="12" md="4" class="mt-5 mx-auto">
          <b-card>
            <b-card-header class="text-center">
              <p>Selected Plan:</p>
              <h4 class="font-weight-bold">{{ company.plan.name }}</h4>
            </b-card-header>
            <b-card-body class="mb-4">
              <h3 class="b-card-title text-center">
                {{ company.plan.price | formatMoney }}
                <small class="text-muted">/ mo</small>
              </h3>
              <b-list-group flush>
                <b-list-group-item>
                  {{ company.plan.jobs }} Job per month
                  <font-awesome-icon
                    :icon="['fas', 'check']"
                    class="m-auto text-success"
                  />
                </b-list-group-item>
                <b-list-group-item>
                  {{ company.plan.logins }} Login
                  <font-awesome-icon
                    :icon="['fas', 'check']"
                    class="m-auto text-success"
                  />
                </b-list-group-item>
              </b-list-group>
            </b-card-body>
            <b-button
              size="lg"
              block
              variant="primary"
              @click="$refs.stepper.previous()"
              >Change</b-button
            >
          </b-card>
        </b-col>
        <b-col cols="12" class="mt-5">
          <b-button
            v-if="company.plan.price > 0"
            size="lg"
            variant="primary"
            @click="$refs.stepper.next()"
            >Proceed to payment</b-button
          >
          <b-button
            v-else
            size="lg"
            variant="primary"
            @click="UpdateAndContinue"
            >Save and continue to profile</b-button
          >
        </b-col>
      </b-row>
    </template>

    <template v-if="step === 3">
      <p>Pay</p>
      <b-col cols="12" md="12" class="mt-5 mb-4">
        <b-row>
          <b-col cols="12" md="6" class="mt-3">
            <label>Name on card:</label>
            <b-form-input placeholder="John Smith" />
          </b-col>
          <b-col cols="12" md="6" class="mt-3">
            <label>Card Number:</label>
            <b-form-input placeholder="1024 2345 1234 1234" maxlength="19" />
          </b-col>
          <b-col cols="12" md="6" class="mt-3">
            <label>Expiry:</label>
            <b-row>
              <b-col cols="3">
                <b-form-input type="text" placeholder="Month" /> </b-col
              >/
              <b-col cols="3">
                <b-form-input type="text" placeholder="Year" />
              </b-col>
            </b-row>
          </b-col>
          <b-col cols="12" md="2" class="mt-3">
            <label>CVC:</label>
            <b-form-input type="text" placeholder="111" />
          </b-col>
        </b-row>
      </b-col>
      <b-button
        size="lg"
        variant="primary"
        class="text-center"
        @click="UpdateAndContinue"
        >Pay {{ company.plan.price | formatMoney }}</b-button
      >
    </template>
  </b-col>
</template>
<script>
import VStepper from "vue-stepper-component";
import firebase from "@/Firebase";
import auth from "@/fstore/auth";
import Company from "@/fstore/collections/Company";
import User from "@/fstore/collections/User";
import GoogleAutocomplete from "vue-google-autocomplete";

export default {
  components: {
    VStepper,
    GoogleAutocomplete
  },
  data() {
    return {
      steps: 3,
      step: undefined,
      plans: {
        basic: {
          name: "Basic",
          jobs: 1,
          logins: 1,
          questions: 2,
          price: 0
        },
        starter: {
          name: "Starter",
          jobs: 10,
          logins: 2,
          questions: 5,
          price: 9900
        },
        unlimited: {
          name: "Unlimited",
          jobs: "unlimited",
          logins: "unlimited",
          questions: 10,
          price: 19900
        }
      },
      company: {},
      user: this.$store.state.auth.data,
      provinces: [
        { value: "PE", text: "Prince Edward Island" },
        { value: "NB", text: "New Brunswick" },
        { value: "NS", text: "Nova Scotia" },
        { value: "NL", text: "Newfoundland & Laborador" },
        { value: "QC", text: "Quebec" },
        { value: "ON", text: "Ontario" },
        { value: "MB", text: "Manitoba" },
        { value: "SK", text: "Saskatchewan" },
        { value: "AB", text: "Alberta" },
        { value: "BC", text: "British Columbia" },
        { value: "YT", text: "Yukon" },
        { value: "NW", text: "Northwest Terrirories" },
        { value: "NU", text: "Nunavut" }
      ],
      states: [{ value: "WA", text: "Washington" }]
    };
  },
  async created() {
    this.company = await Company.getByUserId(this.user.uid);
    console.log(this.company);
    this.getAddressData(null);
  },
  methods: {
    selectPlan(type) {
      this.company.plan = this.plans[type.toLowerCase()];
      this.$refs.stepper.next();
    },
    getAddressData(address) {
      let formatted_address = "";

      if (!this.company.address) this.company.address = {};

      this.company.address.line1 = `${address.street_number} ${address.route}`;

      if (typeof address.street_number !== "undefined")
        formatted_address += `${address.street_number} `;

      if (typeof address.route !== "undefined")
        formatted_address += `${address.route}, `;

      if (typeof address.locality !== "undefined") {
        this.company.address.city = address.locality;
        formatted_address += `${address.locality}, `;
      }

      if (typeof address.administrative_area_level_1 !== "undefined") {
        this.company.address.state = address.administrative_area_level_1;
        formatted_address += `${address.administrative_area_level_1}, `;
      }

      if (typeof address.country !== "undefined") {
        formatted_address += `${address.country}`;
        this.company.address.country = address.country;
      }

      this.company.address.postal_code = address.postal_code;

      this.user.address = formatted_address;
      this.company.address.formatted = formatted_address;
    },
    async UpdateAndContinue() {
      this.user.setup = true;
      await this.$store.dispatch('auth/update', this.user);
      this.$store.dispatch("app/setOverlay", true);
      this.$store.dispatch("company/update", this.company);
      this.$store.dispatch("app/setOverlay", false);
      this.$router.push({name:'companyJobs', query:{id: this.company.uid}});
      
    }
  }
};
</script>